import SiteManagerView from "./SiteManagerView"
import Localization from "@/Localization"
import SliderControl from "./SliderControl"
import { SiteWizardViewModel, WizardInfoViewModel, WizardRegionInfoViewModel, WizardClassifierInfoViewModel, WizardCategorySystemInfoViewModel, WizardVpsMeasurementsInfoViewModel, WizardFpsOptionsInfoViewModel } from "@/TemsManagerWebInterface"
import { WizardDeviceInfoDeviceCategory as WizardDeviceCategory, WizardFpsSetup } from "@/TemsManagerWebEnums"
import { WizardValidityStates, WizardValidator } from "./WizardValidator"
import { WizardElementInfoViewModel, WizardDeviceInfoViewModel } from "@/TemsManagerWebInterface";
import { ViewModelUserLevel } from 'Tems/Security/TemsPolicy';

export enum WizardMode {
    None,
    Fps,
    Tic,
    Tic501, // this special case has been added later. Tic501 should not contain axle devices
    Vps,
}

export default class SiteWizardView {

    private static get MAX_DEVICE_COUNT(): number { return 4; }
    private static get MIN_VPS_LMS_WIDTH_DISTANCE(): number { return 2.0; }
    private static get WIZARD_INDEX_CLASS(): string { return "wizardIndexClick"; }

    private siteManagerView: SiteManagerView;
    private wizardModal: JQuery;
    private editArea: JQuery;
    private loadingGif: JQuery;
    private currentMode: WizardMode;
    private wizardReset: boolean;

    private wizardInfo: WizardInfoViewModel;
    private selectedRegionInfo: WizardRegionInfoViewModel;
    private selectedClassifierInfo: WizardClassifierInfoViewModel;
    private selectedCategorySystemInfo: WizardCategorySystemInfoViewModel;
    private currentVpsMeasurementsInfo: WizardVpsMeasurementsInfoViewModel;
    private currentFpsOptionsInfo: WizardFpsOptionsInfoViewModel;

    private wizardViewModel: SiteWizardViewModel;
    private wizardSettingsLocationId: string;
    private captureDeviceTypeIds: string[];
    private lastActiveDeviceTypeId: string;
    private currentLocationType: string; // NOTE: Make it a property?
    private currentPage: WizardPage;
    private validityStates: WizardValidityStates;

    constructor(siteManager: SiteManagerView) {
        this.wizardModal = $("#modalWizard");
        this.editArea = this.wizardModal.find("#modalWizardEditArea");
        this.loadingGif = this.wizardModal.find("#modalWizardLoading");
        this.currentMode = WizardMode.None;
        this.siteManagerView = siteManager;
        this.wizardSettingsLocationId = null;
        this.captureDeviceTypeIds = [];
        this.lastActiveDeviceTypeId = null;
        this.validityStates = {
            Locations: false,
            Components: false,
            AxleDetection: false,
            FpsOptions: false,
            Region: false,
            Classifier: false,
            VpsMeasurements: false
        };
        this.wizardReset = false;
        this.resetWizardViewModel();
    }

    // opens the modal and adds settings of existing wizard configurations
    public startWizard(): void {
        this.editArea.html("");
        this.toggleWizardMode();
        this.toggleButtonEnabled();
        this.wizardModal.find("#modalWizardRoads").unbind().on("click", (event) => { this.goToSpecificPage(WizardPage.Locations, $(event.currentTarget)); });
        this.wizardModal.find("#modalWizardDevices").unbind().on("click", (event) => { this.goToSpecificPage(WizardPage.Devices, $(event.currentTarget)); });
        this.wizardModal.find("#modalWizardAxleDetection").unbind().on("click", (event) => { this.goToSpecificPage(WizardPage.AxleDetection, $(event.currentTarget)); });
        this.wizardModal.find("#modalWizardFpsOptions").unbind().on("click", (event) => { this.goToSpecificPage(WizardPage.FpsOptions, $(event.currentTarget)); });
        this.wizardModal.find("#modalWizardRegion").unbind().on("click", (event) => { this.goToSpecificPage(WizardPage.Region, $(event.currentTarget)); });
        this.wizardModal.find("#modalWizardClassifier").unbind().on("click", (event) => { this.goToSpecificPage(WizardPage.Classifier, $(event.currentTarget)); });
        this.wizardModal.find("#modalWizardVpsMeasurements").unbind().on("click", (event) => { this.goToSpecificPage(WizardPage.VpsMeasurements, $(event.currentTarget)); });
        this.wizardModal.find("#modalWizardPrevPageButton").unbind().on("click", () => this.goToPrevPage());
        this.wizardModal.find("#modalWizardNextPageButton").unbind().on("click", () => this.goToNextPage());
        this.wizardModal.find("#modalWizardCreateSiteButton").unbind().on("click", () => this.createSiteFromWizard());
        this.wizardModal.modal("show");
        this.resetWizardViewModel();
        this.updateValues();

        this.loadWizardInfo(true);
    }

    private loadWizardInfo(startup = false): void {
        this.loadingGif.show();
        this.disableIndexClick();
        this.siteManagerView.changeUserLevel(ViewModelUserLevel.AuthorizedClient).then(() => {
            $.ajax({
                url: `/api/SiteWizard/GetWizardInfoViewModel`
            }).done((model: WizardInfoViewModel) => {
                this.loadingGif.hide();
                if (model != null) {
                    this.wizardInfo = model;
                } else {
                    this.wizardInfo = {
                        Locations: null,
                        MaxDevicesCount: SiteWizardView.MAX_DEVICE_COUNT,
                        Regions: null,
                        Devices: null
                    };
                }

                this.loadWizardConfig(startup);
            }).fail(err => {
                this.loadingGif.hide();
                this.wizardModal.modal("hide");
                this.siteManagerView.showAlertMessage(
                    Localization.resourceStrings.SiteWizardModal_ErrorStartWizard,
                    err.responseJSON.Message,
                    Localization.resourceStrings.SiteView_ErrorExceptionMessageTemplate
                );
            });
        });
    }

    private loadWizardConfig(startup = false): void {
        this.siteManagerView.changeUserLevel(ViewModelUserLevel.AuthorizedClient).then(() => {
            this.loadingGif.show();
            this.disableIndexClick();
            $.ajax({
                url: `/api/SiteWizard/GetWizardViewModel`
            }).done((model: SiteWizardViewModel) => {
                this.loadingGif.hide();
                if (model != null) {
                    this.wizardViewModel = model;
                } else {
                    this.resetWizardViewModel();
                }

                this.wizardSettingsLocationId = this.wizardViewModel.LocationId;
                if (this.wizardViewModel.CapturingDevices == null && this.wizardInfo != null && this.wizardInfo.Devices != null) {
                    this.wizardViewModel.CapturingDevices = this.wizardInfo.Devices.filter((device) => { return device.Category === WizardDeviceCategory.Capturing });
                }

                this.updateCaptureDeviceTypeIds(this.wizardViewModel.CapturingDevices);
                this.getActiveDeviceTypeId();
                this.selectedRegionInfo = null;
                this.selectedClassifierInfo = null;
                this.selectedCategorySystemInfo = null;
                this.currentVpsMeasurementsInfo = this.wizardViewModel.VpsMeasurements;
                this.currentFpsOptionsInfo = this.wizardViewModel.FpsOptions;

                if (this.wizardViewModel.RegionFileName != null && this.wizardInfo != null && this.wizardInfo.Regions != null) {
                    for (let region of this.wizardInfo.Regions) {
                        if (this.wizardViewModel.RegionFileName === region.Name) {
                            this.selectedRegionInfo = region;
                        }
                    }
                }

                if (this.wizardViewModel.ClassifierFileName != null && this.selectedRegionInfo != null && this.selectedRegionInfo.Classifiers != null) {
                    for (let classifier of this.selectedRegionInfo.Classifiers) {
                        if (this.wizardViewModel.ClassifierFileName === classifier.Name) {
                            this.selectedClassifierInfo = classifier;
                        }
                    }
                }

                if (this.wizardViewModel.CategorySystemFileName != null &&
                    this.selectedClassifierInfo != null &&
                    this.selectedClassifierInfo.CategorySystem != null
                    && this.selectedClassifierInfo.CategorySystem.CategorySystems != null) {

                    for (let catSys of this.selectedClassifierInfo.CategorySystem.CategorySystems) {
                        if (this.wizardViewModel.CategorySystemFileName === catSys.Name) {
                            this.selectedCategorySystemInfo = catSys;
                        }
                    }
                }

                this.wizardReset = false;
                this.updateValues();
                if (startup) {
                    this.showLocationEdit();
                }
            }).fail(err => {
                this.loadingGif.hide();
                this.wizardModal.modal("hide");
                this.siteManagerView.showAlertMessage(
                    Localization.resourceStrings.SiteWizardModal_ErrorStartWizard,
                    err.responseJSON.Message,
                    Localization.resourceStrings.SiteView_ErrorExceptionMessageTemplate
                );
            });
        });
    }

    private resetWizardViewModel(): void {
        // reset device counts
        if (this.wizardInfo != null && this.wizardInfo.Devices != null) {
            for (let device of this.wizardInfo.Devices) {
                device.Count = 0;
            }
        }

        // reset current selected values
        this.selectedRegionInfo = null;
        this.selectedClassifierInfo = null;
        this.selectedCategorySystemInfo = null;
        this.currentFpsOptionsInfo = null;
        this.currentVpsMeasurementsInfo = null;

        // reset viewModel
        this.wizardViewModel = {
            LocationId: null,
            HasHardShoulder: true,
            AxleDetectionDevices: null,
            CapturingDevices: null,
            CategorySystemFileName: null,
            ClassifierFileName: null,
            RegionFileName: null,
            VpsMeasurements: null,
            FpsOptions: null,
        };

        this.wizardReset = true;
    }

    // sends current wizard config to server to create a new site based on wizard settings
    private createSiteFromWizard(): void {
        this.wizardModal.find("#modalWizardCreateSiteButton").hide();

        if (this.getValidState()) {
            this.wizardViewModel.HasHardShoulder = (this.currentMode === WizardMode.Fps || this.currentMode === WizardMode.Tic || this.currentMode === WizardMode.Tic501) && this.wizardViewModel.HasHardShoulder;
            this.wizardViewModel.RegionFileName = this.selectedRegionInfo.Name;
            this.wizardViewModel.ClassifierFileName = (this.currentMode === WizardMode.Fps || this.currentMode === WizardMode.Tic || this.currentMode === WizardMode.Tic501) ? this.selectedClassifierInfo.Name : null;
            this.wizardViewModel.CategorySystemFileName = this.selectedCategorySystemInfo != null && this.wizardViewModel.ClassifierFileName != null ? this.selectedCategorySystemInfo.Name : null;
            this.wizardViewModel.VpsMeasurements = this.currentMode === WizardMode.Vps ? this.currentVpsMeasurementsInfo : undefined; // this needs to be undefined. Trying to submit null to the controller results in the default viewModel constructor being called. o_O ?
            this.wizardViewModel.FpsOptions = this.currentMode === WizardMode.Fps ? this.currentFpsOptionsInfo : null;
            this.wizardModal.find("#modalWizardCreateSiteButton").prop("disabled", true);

            // send wizard model to controller
            this.siteManagerView.changeUserLevel(ViewModelUserLevel.AuthorizedClient).then(() => {
                $.ajax({
                    url: `/api/SiteWizard/CreateSiteFromWizard`,
                    type: "POST",
                    data: this.wizardViewModel
                }).done(() => {
                    this.siteManagerView.siteView.requestSiteScene();
                    this.wizardModal.modal("hide");
                }).fail(err => {
                    this.siteManagerView.showAlertMessage(
                        Localization.resourceStrings.SiteWizardModal_ErrorCreateSiteFromWizard,
                        err.responseJSON.Message,
                        Localization.resourceStrings.SiteView_ErrorExceptionMessageTemplate
                    );

                    this.toggleButtonEnabled();
                });
            });
        }
    }

    // loads content of region page
    private showRegionEdit(): void {
        const tbody = $("<tbody>");

        this.currentPage = WizardPage.Region;
        this.toggleButtonEnabled();

        this.editArea.html("");
        this.editArea
            .append($("<h4>", { html: Localization.resourceStrings.SiteWizardModal_EditRegionTitle }))
            .append($("<table>", { "class": "table table-noTopBorder margin-bottom-0", cellspacing: 0 })
                .append($("<thead>")
                    .append($("<tr>")
                        .append($("<th>", { html: Localization.resourceStrings.SiteWizardModal_EditTableNameTitle }))
                        .append($("<th>", { html: Localization.resourceStrings.SiteWizardModal_EditTableDescriptionTitle }))))
                .append(tbody));

        for (let region of this.wizardInfo.Regions) {

            // for mode vps no classifier is needed, for tic and tic501 classifiers are mandatory
            if (this.currentMode === WizardMode.Vps || region.Classifiers.length > 0) {
                if (this.selectedRegionInfo == null) {
                    this.selectedRegionInfo = region;
                    this.selectedClassifierInfo = null;
                    this.selectedCategorySystemInfo = null;
                }

                tbody
                    .append($("<tr>", {
                        "class": `wizardSelect ${this.selectedRegionInfo.Name === region.Name ? "active" : ""}`,
                        "data-value": region.Name
                    })
                        .on("click", this.createTableClickHandlerDataFile(region, DataFileType.Region))
                        .append($("<td>", { html: region.Title, "class": "valueColumn" }))
                        .append($("<td>", { html: region.Description })));
            }
        }

        this.updateValues();
    }

    // loads content of component page
    private showLocationEdit(): void {

        this.currentPage = WizardPage.Locations;
        this.toggleButtonEnabled();

        this.createLocationsList();
    }

    // loads content of component page
    private showDeviceEdit(): void {

        this.currentPage = WizardPage.Devices;
        this.toggleButtonEnabled();

        this.createCaptureDevicesList();
    }

    // loads content of classifier page
    private showClassifierEdit(): void {

        let classifierContainer: JQuery = $("<div>");
        let catSysContainer: JQuery = $("<div>", { "style": "margin-top: 1em;" });
        let tbodyClassifier: JQuery = $("<tbody>");

        this.currentPage = WizardPage.Classifier;
        this.toggleButtonEnabled();

        this.editArea.html("");
        this.editArea
            .append($("<div>")
                .append(classifierContainer)
                .append(catSysContainer));

        classifierContainer
            .append($("<h4>", { html: Localization.resourceStrings.SiteWizardModal_EditClassifierTitle }))
            .append($("<table>", { "class": "table table-noTopBorder margin-bottom-0", cellspacing: 0 })
                .append($("<thead>")
                    .append($("<tr>")
                        .append($("<th>", { html: Localization.resourceStrings.SiteWizardModal_EditTableNameTitle, "class": "col-xs-3" }))
                        .append($("<th>", { html: Localization.resourceStrings.SiteWizardModal_EditTableDescriptionTitle, "class": "col-xs-9" }))))
                .append(tbodyClassifier));

        catSysContainer
            .append($("<h4>", { html: Localization.resourceStrings.SiteWizardModal_EditCatSysTitle }))
            .append($("<table>", { "class": "table table-noTopBorder margin-bottom-0", cellspacing: 0 })
                .append($("<thead>")
                    .append($("<tr>")
                        .append($("<th>", { html: Localization.resourceStrings.SiteWizardModal_EditTableNameTitle, "class": "col-xs-3" }))
                        .append($("<th>", { html: Localization.resourceStrings.SiteWizardModal_EditTableDescriptionTitle, "class": "col-xs-9" }))))
                .append($("<tbody>", { id: "tableCategorySystemSelection" })
                    .append($("<div>", { html: "Select classifier first" }))));

        for (let classifier of this.selectedRegionInfo.Classifiers) {
            if (this.selectedClassifierInfo == null) {
                this.selectedClassifierInfo = classifier;
            }

            tbodyClassifier
                .append($("<tr>", { "class": `wizardSelect ${this.selectedClassifierInfo === classifier ? "active" : ""}`, "data-value": classifier.Name })
                    .on("click", this.createTableClickHandlerDataFile(classifier, DataFileType.Classifier))
                    .append($("<td>", { html: classifier.Title, "class": "valueColumn" }))
                    .append($("<td>", { html: classifier.Description })));

            this.updateValues();

            if (this.selectedClassifierInfo === classifier) {
                this.createCatSysTableBody();
            }
        }

        setTimeout(() => {
            if (tbodyClassifier.children("tr.active").length === 0) {
                tbodyClassifier.children("tr").first().click();
            } else {
                tbodyClassifier.children("tr.active").first().click();
            }
        }, 0);
    }

    // loads content of axle counting page
    private showAxleDetectionEdit(): void {

        this.currentPage = WizardPage.AxleDetection;
        this.toggleButtonEnabled();

        this.createAxleDevicesList();
    }

    // loads content of measurements page
    private showVpsMeasurementsEdit(): void {

        this.currentPage = WizardPage.VpsMeasurements;
        this.toggleButtonEnabled();

        this.updateVpsMeasurementInfoValues();

        // initialize sliders
        let vehicleHeightMax = new SliderControl(Localization.resourceStrings.SiteWizardModal_EditVpsMeasurementsSliderLabelHeight, 0.0, 10.0, 0.001, "m", (id, newValue) => { this.currentVpsMeasurementsInfo.VehicleMaxHeight = newValue; this.updateValues(); }, "VpsMeasurementsVehicle", `wizardVpsMeasurementsVehicleMaxHeight`, true);
        let vehicleWidthMax = new SliderControl(Localization.resourceStrings.SiteWizardModal_EditVpsMeasurementsSliderLabelWidth, 0.0, 10.0, 0.001, "m", (id, newValue) => { this.currentVpsMeasurementsInfo.VehicleMaxWidth = newValue; this.updateValues(); }, "VpsMeasurementsVehicle", `wizardVpsMeasurementsVehicleMaxWidth`, true);
        let vehicleLengthMax = new SliderControl(Localization.resourceStrings.SiteWizardModal_EditVpsMeasurementsSliderLabelLength, 0.0, 40.0, 0.001, "m", (id, newValue) => { this.currentVpsMeasurementsInfo.VehicleMaxLength = newValue; this.updateValues(); }, "VpsMeasurementsVehicle", `wizardVpsMeasurementsVehicleMaxLength`, true);
        let lmsHeightWidth = new SliderControl(Localization.resourceStrings.SiteWizardModal_EditVpsMeasurementsSliderLabelLmsWidthHeight, 0.0, 10.0, 0.001, "m", (id, newValue) => { this.currentVpsMeasurementsInfo.LmsHeightWidth = newValue; this.updateValues(); }, "VpsMeasurementsLms", `wizardVpsMeasurementsLmsHeightWidth`, true);
        let lmsHeightLength = new SliderControl(Localization.resourceStrings.SiteWizardModal_EditVpsMeasurementsSliderLabelLmsLengthHeight, 0.0, 10.0, 0.001, "m", (id, newValue) => { this.currentVpsMeasurementsInfo.LmsHeightLength = newValue; this.updateValues(); }, "VpsMeasurementsLms", `wizardVpsMeasurementsLmsHeightHeight`, true);
        let lmsDistanceWidth = new SliderControl(Localization.resourceStrings.SiteWizardModal_EditVpsMeasurementsSliderLabelDistanceWidth, this.currentVpsMeasurementsInfo.LmsDistanceWidthMin, this.currentVpsMeasurementsInfo.LmsDistanceWidthMin + 10.0, 0.001, "m", (id, newValue) => { this.currentVpsMeasurementsInfo.LmsDistanceWidth = newValue; this.updateValues(); }, "VpsMeasurementsLms", `wizardVpsMeasurementsLmsWidth`, true);
        let lmsDistanceLength = new SliderControl(Localization.resourceStrings.SiteWizardModal_EditVpsMeasurementsSliderLabelDistanceLength, SiteWizardView.MIN_VPS_LMS_WIDTH_DISTANCE, 40.0, 0.001, "m", (id, newValue) => { this.currentVpsMeasurementsInfo.LmsDistanceLength = newValue; this.updateValues(); }, "VpsMeasurementsLms", `wizardVpsMeasurementsLmsLength`, true);

        let vehicleDimensionSettings: JQuery = $("<div>", { "class": "col-xs-12", "style": "margin-top: 1em;" })
            .append($("<div>", { "class": "row" })
                .append($("<h4>", { "class": "col-xs-12", html: Localization.resourceStrings.SiteWizardModal_EditVpsMeasurementsVehicleDimensionsRowTitle })))
            .append($("<div>", { "class": "row" })
                .append($("<div>", { "class": "col-xs-12 col-sm-5", html: vehicleWidthMax.Label }))
                .append($("<div>", { "class": "col-xs-12 col-sm-2 col-sliderValue", html: vehicleWidthMax.Value }))
                .append($("<div>", { "class": "col-xs-12 col-sm-5", html: vehicleWidthMax.Slider })))
            .append($("<div>", { "class": "row" })
                .append($("<div>", { "class": "col-xs-12 col-sm-5", html: vehicleLengthMax.Label }))
                .append($("<div>", { "class": "col-xs-12 col-sm-2 col-sliderValue", html: vehicleLengthMax.Value }))
                .append($("<div>", { "class": "col-xs-12 col-sm-5", html: vehicleLengthMax.Slider })))
            .append($("<div>", { "class": "row" })
                .append($("<div>", { "class": "col-xs-12 col-sm-5", html: vehicleHeightMax.Label }))
                .append($("<div>", { "class": "col-xs-12 col-sm-2 col-sliderValue", html: vehicleHeightMax.Value }))
                .append($("<div>", { "class": "col-xs-12 col-sm-5", html: vehicleHeightMax.Slider })));

        let lmsPositioningSettings: JQuery = $("<div>", { "class": "col-xs-12" })
            .append($("<div>", { "class": "row" })
                .append($("<h4>", { "class": "col-xs-11", html: Localization.resourceStrings.SiteWizardModal_EditVpsMeasurementsLmsPositioningRowTitle })))
            .append($("<div>", { "class": "row" })
                .append($("<div>", { "class": "col-xs-12 col-sm-5", html: lmsDistanceWidth.Label }))
                .append($("<div>", { "class": "col-xs-12 col-sm-2 col-sliderValue", html: lmsDistanceWidth.Value }))
                .append($("<div>", { "class": "col-xs-12 col-sm-5", html: lmsDistanceWidth.Slider })))
            .append($("<div>", { "class": "row" })
                .append($("<div>", { "class": "col-xs-12 col-sm-5", html: lmsDistanceLength.Label }))
                .append($("<div>", { "class": "col-xs-12 col-sm-2 col-sliderValue", html: lmsDistanceLength.Value }))
                .append($("<div>", { "class": "col-xs-12 col-sm-5", html: lmsDistanceLength.Slider })))
            .append($("<div>", { "class": "row" })
                .append($("<div>", { "class": "col-xs-12 col-sm-5", html: lmsHeightWidth.Label }))
                .append($("<div>", { "class": "col-xs-12 col-sm-2 col-sliderValue", html: lmsHeightWidth.Value }))
                .append($("<div>", { "class": "col-xs-12 col-sm-5", html: lmsHeightWidth.Slider })))
            .append($("<div>", { "class": "row" })
                .append($("<div>", { "class": "col-xs-12 col-sm-5", html: lmsHeightLength.Label }))
                .append($("<div>", { "class": "col-xs-12 col-sm-2 col-sliderValue", html: lmsHeightLength.Value }))
                .append($("<div>", { "class": "col-xs-12 col-sm-5", html: lmsHeightLength.Slider })));

        let lmsPositioningOverviewImage: JQuery = $("<div>", { "class": "col-xs-12" })
            .append($("<img>", { src: "/wizard/VpsSchema.jpg", 'aria-hidden': "true", 'style': "width:100%;" }));

        // set default values for sliders
        vehicleHeightMax.setValue(this.currentVpsMeasurementsInfo.VehicleMaxHeight);
        vehicleWidthMax.setValue(this.currentVpsMeasurementsInfo.VehicleMaxWidth);
        vehicleLengthMax.setValue(this.currentVpsMeasurementsInfo.VehicleMaxLength);
        lmsHeightWidth.setValue(this.currentVpsMeasurementsInfo.LmsHeightWidth);
        lmsHeightLength.setValue(this.currentVpsMeasurementsInfo.LmsHeightLength);
        lmsDistanceWidth.setValue(this.currentVpsMeasurementsInfo.LmsDistanceWidth);
        lmsDistanceLength.setValue(this.currentVpsMeasurementsInfo.LmsDistanceLength);

        this.editArea.html("");
        this.editArea
            .append($("<div>", { "class": "container-fluid" })
                .append($("<div>", { "class": "row" })
                    .append($("<div>", { "class": "col-xs 12 col-md-6" })
                        .append($("<div>", { "class": "row" }).append(lmsPositioningSettings))
                        .append($("<div>", { "class": "row" }).append(vehicleDimensionSettings)))
                    .append($("<div>", { "class": "col-xs 12 col-md-6" })
                        .append(lmsPositioningOverviewImage))));
    }

    // loads content of measurements page
    private showFpsOptionsEdit(): void {

        this.currentPage = WizardPage.FpsOptions;
        this.toggleButtonEnabled();

        if (!this.currentFpsOptionsInfo) {
            this.currentFpsOptionsInfo = { FpsSetup: WizardFpsSetup.SingleLane };
        }

        let imageSingleLane = $("<img>", { src: "/wizard/FpsSingleLane.jpg", 'aria-hidden': "true", 'style': "width:100%;", "data-value": WizardFpsSetup.SingleLane })
        let imageSingleLaneWithAC = $("<img>", { src: "/wizard/FpsSingleLaneAC.jpg", 'aria-hidden': "true", 'style': "width:100%;", "data-value": WizardFpsSetup.SingleLaneWithAC })
        let imageDoubleLane = $("<img>", { src: "/wizard/FpsDoubleLane.jpg", 'aria-hidden': "true", 'style': "width:100%;", "data-value": WizardFpsSetup.DoubleLaneShared })
        let fpsSiteImages = $("<div>", { "id": "fpsImagesContainer", "class": "col-xs-12 col-sm-8  col-md-10" })
            .append(imageSingleLane.toggle(this.currentFpsOptionsInfo.FpsSetup === WizardFpsSetup.SingleLane))
            .append(imageSingleLaneWithAC.toggle(this.currentFpsOptionsInfo.FpsSetup === WizardFpsSetup.SingleLaneWithAC))
            .append(imageDoubleLane.toggle(this.currentFpsOptionsInfo.FpsSetup === WizardFpsSetup.DoubleLaneShared));
        let tbody = $("<tbody>");

        tbody
            .append($("<tr>", { "class": `wizardSelect ${this.currentFpsOptionsInfo.FpsSetup === WizardFpsSetup.SingleLane ? "active" : ""}`, "data-value": WizardFpsSetup.SingleLane })
                .on("click", this.createTableClickHandlerFpsSetup())
                .append($("<td>", { html: Localization.resourceStrings.SiteWizardModal_EnumFpsSetupSingleLane, "class": "valueColumn" })))
            .append($("<tr>", { "class": `wizardSelect ${this.currentFpsOptionsInfo.FpsSetup === WizardFpsSetup.SingleLaneWithAC ? "active" : ""}`, "data-value": WizardFpsSetup.SingleLaneWithAC })
                .on("click", this.createTableClickHandlerFpsSetup())
                .append($("<td>", { html: Localization.resourceStrings.SiteWizardModal_EnumFpsSetupSingleLaneWithAC, "class": "valueColumn" })))
            .append($("<tr>", { "class": `wizardSelect ${this.currentFpsOptionsInfo.FpsSetup === WizardFpsSetup.DoubleLaneShared ? "active" : ""}`, "data-value": WizardFpsSetup.DoubleLaneShared })
                .on("click", this.createTableClickHandlerFpsSetup())
                .append($("<td>", { html: Localization.resourceStrings.SiteWizardModal_EnumFpsSetupDoubleLane, "class": "valueColumn" })))      

        this.editArea.html("");
        this.editArea
            .append($("<h4>", { html: Localization.resourceStrings.SiteWizardModal_EditFpsOptionsTitle }))
            .append($("<div>", { "class": "row" })
                .append($("<div>", { "class": "col-xs-12 col-md-9 col-lg-6" })
                    .append($("<table>", { "class": "table table-noTopBorder margin-bottom-0", cellspacing: 0 })
                        .append(tbody)))
                .append(fpsSiteImages));

        this.createAxleDevicesList();
        this.updateValues();
    }

    // adds a click handler to a table with data files to choose from
    private createTableClickHandlerFpsSetup(): (event: JQueryEventObject) => void {

        return (event: JQueryEventObject) => {
            event.preventDefault();
            let element = $(event.currentTarget);
            let fpsAxleDevices = this.wizardViewModel.AxleDetectionDevices.filter(d => d.LocationTypeName === this.currentLocationType);

            this.currentFpsOptionsInfo.FpsSetup = element.data("value") as WizardFpsSetup;
            this.wizardModal.find("#fpsImagesContainer").children("img").each((idx, child) => {
                $(child).toggle($(child).data("value") === element.data("value"));
            });
            switch (this.currentFpsOptionsInfo.FpsSetup) {
                case WizardFpsSetup.SingleLane:
                    if (fpsAxleDevices && fpsAxleDevices.length === 1) {
                        fpsAxleDevices[0].Count = 0;
                    }
                    break;
                case WizardFpsSetup.SingleLaneWithAC:
                    if (fpsAxleDevices && fpsAxleDevices.length === 1) {
                        fpsAxleDevices[0].Count = 1;
                    }
                    break;
                case WizardFpsSetup.DoubleLaneShared:
                    if (fpsAxleDevices && fpsAxleDevices.length === 1) {
                        fpsAxleDevices[0].Count = 0;
                    }
                    break;
            }

            element.siblings().removeClass("active");
            element.addClass("active");

            this.updateValues();
        };
    }

    // adds a click handler to a table with data files to choose from
    private createTableClickHandlerDataFile(elementInfo: WizardElementInfoViewModel, type: DataFileType): (event: JQueryEventObject) => void {

        return (event: JQueryEventObject) => {
            event.preventDefault();
            let element = $(event.currentTarget);

            switch (type) {
                case DataFileType.Region:
                    if (elementInfo != null) {
                        if (this.selectedRegionInfo !== elementInfo) {
                            this.selectedClassifierInfo = null;
                            this.currentVpsMeasurementsInfo = null;
                        }

                        this.selectedRegionInfo = elementInfo as WizardRegionInfoViewModel;
                    }
                    break;
                case DataFileType.Classifier:
                    if (elementInfo != null) {
                        this.selectedClassifierInfo = elementInfo as WizardClassifierInfoViewModel;
                        this.createCatSysTableBody();
                    }
                    break;
                case DataFileType.CategorySystem:
                    this.selectedCategorySystemInfo = elementInfo as WizardCategorySystemInfoViewModel;
                    break;
                default:
                    break;
            }

            element.siblings().removeClass("active");
            element.addClass("active");

            this.updateValues();
        };
    }

    // adds count controls to a column (Format: minusButton, currentCount, plusButton)
    private createCountColumn(device: WizardDeviceInfoViewModel): JQuery {

        let parentElement: JQuery = $("<td>");
        let decreaseCount: JQuery = $("<span>", { "class": "wizardDecreaseCount glyphicon glyphicon-minus" });
        let increaseCount: JQuery = $("<span>", { "class": "wizardIncreaseCount glyphicon glyphicon-plus" });
        let countValue: JQuery = $("<span>", { "class": "countValue", html: device.Count });

        parentElement
            .addClass("countControlElement")
            .toggleClass("maxCountReached", device.Count >= device.MaxCount)
            .append(decreaseCount)
            .append(countValue)
            .append(increaseCount);

        decreaseCount.on("click", () => {
            let modelDevice = this.wizardViewModel.CapturingDevices.filter(d => { return d.Title === device.Title && d.LocationTypeName === device.LocationTypeName; });
            if (modelDevice.length === 0) {
                modelDevice = this.wizardViewModel.AxleDetectionDevices.filter(d => { return d.Title === device.Title && d.LocationTypeName === device.LocationTypeName; });
            }

            if (modelDevice.length > 0) {
                if (modelDevice[0].Count > 0) {
                    countValue.html((--modelDevice[0].Count).toString());
                }

                parentElement.toggleClass("maxCountReached", modelDevice[0].Count === modelDevice[0].MaxCount);
                this.updateValues();
            }
        });

        increaseCount.on("click", (event: JQueryEventObject) => {
            let modelDevice = this.wizardViewModel.CapturingDevices.filter(d => { return d.Title === device.Title && d.LocationTypeName === device.LocationTypeName; });
            if (modelDevice.length === 0) {
                modelDevice = this.wizardViewModel.AxleDetectionDevices.filter(d => { return d.Title === device.Title && d.LocationTypeName === device.LocationTypeName; });
            }

            if (modelDevice.length > 0 && !$(event.currentTarget).hasClass("iconDisabled")) {
                if (modelDevice[0].Count < modelDevice[0].MaxCount) {
                    countValue.html((++modelDevice[0].Count).toString());
                }

                parentElement.toggleClass("maxCountReached", modelDevice[0].Count === modelDevice[0].MaxCount);
                this.updateValues();
            }
        });


        return parentElement;
    }

    // add content to category system table. Updates the available category systems according to the chosen classifier. This should be called when the classifier changes.
    private createCatSysTableBody(): void {

        let tbodyCatsys: JQuery = this.wizardModal.find("tbody#tableCategorySystemSelection");

        if (this.selectedClassifierInfo != null) {
            tbodyCatsys.html("");
            tbodyCatsys
                .append($("<tr>", { "class": `wizardSelect ${this.selectedCategorySystemInfo == null ? "active" : ""}`, "data-value": "" })
                    .on("click", this.createTableClickHandlerDataFile(null, DataFileType.CategorySystem))
                    .append($("<td>", { html: Localization.resourceStrings.SiteWizardModal_NoExtCatSysTitle, "class": "valueColumn" }))
                    .append($("<td>", { html: Localization.resourceStrings.SiteWizardModal_NoExtCatSysDescription })));

            for (let categorySystem of this.selectedClassifierInfo.CategorySystem.CategorySystems) {

                tbodyCatsys
                    .append($("<tr>", {
                        "class": `wizardSelect ${this.selectedCategorySystemInfo != null && this.selectedCategorySystemInfo === categorySystem ? "active" : ""}`,
                        "data-value": categorySystem.Name
                    })
                        .on("click", this.createTableClickHandlerDataFile(categorySystem, DataFileType.CategorySystem))
                        .append($("<td>", { html: categorySystem.Title, "class": "valueColumn" }))
                        .append($("<td>", { html: categorySystem.Description })));

                this.updateValues();
            }

            setTimeout(() => {
                if (tbodyCatsys.children("tr.active").length === 0) {
                    tbodyCatsys.children("tr").first().click();
                } else {
                    tbodyCatsys.children("tr.active").first().click();
                }
            }, 0);
        }
    }

    // update and validation of values inside the summary table
    private updateValues(): void {

        this.toggleCountEnabled();

        this.wizardModal.find("#modalWizardRoadsValue").html(WizardValidator.Locations(this.wizardModal.find("#modalWizardRoads"), this.validityStates, this.wizardViewModel, this.wizardInfo));
        this.wizardModal.find("#modalWizardDevicesValue").html(WizardValidator.Components(this.wizardModal.find("#modalWizardDevices"), this.validityStates, this.wizardViewModel, this.wizardInfo, this.captureDeviceTypeIds));
        this.wizardModal.find("#modalWizardAxleDetectionValue").html(WizardValidator.AxleDetection(this.wizardModal.find("#modalWizardAxleDetection"), this.validityStates, this.wizardViewModel, this.currentLocationType));
        this.wizardModal.find("#modalWizardFpsOptionsValue").html(WizardValidator.FpsOptions(this.wizardModal.find("#modalWizardFpsOptions"), this.validityStates, this.currentFpsOptionsInfo));
        this.wizardModal.find("#modalWizardRegionValue").html(WizardValidator.Region(this.wizardModal.find("#modalWizardRegion"), this.validityStates, this.selectedRegionInfo, this.currentMode));
        this.wizardModal.find("#modalWizardClassifierValue").html(WizardValidator.Classifier(this.wizardModal.find("#modalWizardClassifier"), this.validityStates, this.selectedClassifierInfo, this.currentMode));
        this.wizardModal.find("#modalWizardCatSysValue").html(WizardValidator.CategorySystem(this.validityStates, this.selectedCategorySystemInfo));
        this.wizardModal.find("#modalWizardVpsMeasurementsValue").html(WizardValidator.VpsMeasurements(this.wizardModal.find("#modalWizardVpsMeasurements"), this.validityStates, this.currentVpsMeasurementsInfo));

        this.toggleWizardMode();
        this.toggleButtonEnabled();
        this.toggleIndexClick();
    }

    // returns validity state of the complete wizard model
    private getValidState(): boolean {
        return this.currentMode !== WizardMode.None &&
            this.validityStates.Locations &&
            this.validityStates.Components &&
            (this.validityStates.AxleDetection || this.currentMode !== WizardMode.Tic) &&
            (this.validityStates.FpsOptions || this.currentMode !== WizardMode.Fps) &&
            this.validityStates.Region &&
            (this.validityStates.Classifier || this.currentMode === WizardMode.Vps) &&
            (this.validityStates.VpsMeasurements || this.currentMode !== WizardMode.Vps);
    }

    // sets vpsMeasurementsInfo according to current WizardViewModel and selectedRegion
    private updateVpsMeasurementInfoValues() {
        if (this.currentMode === WizardMode.Vps && this.currentVpsMeasurementsInfo == null) {
            if (this.selectedRegionInfo != null) {
                if (this.wizardViewModel.RegionFileName != null && this.wizardViewModel.RegionFileName === this.selectedRegionInfo.Name && this.wizardViewModel.VpsMeasurements != null) {
                    this.currentVpsMeasurementsInfo = this.wizardViewModel.VpsMeasurements;
                } else {
                    this.currentVpsMeasurementsInfo = this.selectedRegionInfo.VpsMeasurementRegionDefault;
                }
            } else {
                // fallback value, but selectedRegionInfo should always be available at this point
                this.currentVpsMeasurementsInfo = {
                    MinObservationAngle: 10,
                    Lms5xxAngularResolution: 0.25,
                    MaxPointDistanceFront: 0.1,
                    VehicleMaxHeight: 0,
                    VehicleMaxWidth: 0,
                    VehicleMaxLength: 0,
                    LmsHeightWidth: 0,
                    LmsHeightLength: 0,
                    LmsDistanceWidth: 0,
                    LmsDistanceLength: 0,
                    LmsDistanceWidthMin: 0
                };
            }

            this.updateValues();
        }
    }

    // create list of components
    private createLocationsList(): void {
        
        let tbody: JQuery = $("<tbody>");

        this.editArea.html("");
        this.editArea
            .append($("<div>")
                .append($("<h4>", { html: Localization.resourceStrings.SiteWizardModal_EditRoadsTitle }))
                .append($("<table>", { "class": "table table-noTopBorder margin-bottom-0", cellspacing: 0 })
                    .append($("<thead>")
                        .append($("<tr>")
                            .append($("<th>", { html: Localization.resourceStrings.SiteWizardModal_EditRoadsTableTitle, "class": "col-xs-3" }))
                            .append($("<th>", { html: Localization.resourceStrings.SiteWizardModal_EditRoadsTableDescription, "class": "col-xs-9" }))))
                    .append(tbody)));            

        tbody
            .append($("<tr>", { "class": `wizardSelect ${this.wizardViewModel.LocationId === null ? "active" : ""}`, "data-value": null })
                .on("click", (event: JQueryEventObject) => {
                    let element = $(event.currentTarget);
                    this.resetWizardViewModel();
                    this.updateValues();
                    element.siblings().removeClass("active");
                    element.addClass("active");
                })
                .append($("<td>", { html: Localization.resourceStrings.SiteWizardModal_EditRoadsNewRoadTitle, "class": "valueColumn" }))
                .append($("<td>", { html: Localization.resourceStrings.SiteWizardModal_EditRoadsNewRoadDescription })));

        for (let location of this.wizardInfo.Locations) {
            tbody
                .append($("<tr>", { "class": `wizardSelect ${this.wizardViewModel.LocationId === location.Id ? "active" : ""}`, "data-value": location.Id })
                    .on("click", (event: JQueryEventObject) => {
                        let element = $(event.currentTarget);
                        if (location.Id === this.wizardSettingsLocationId && this.wizardReset) {
                            this.loadWizardConfig();
                        }

                        if (location.Id !== this.wizardViewModel.LocationId) {
                            this.resetWizardViewModel();
                            this.wizardViewModel.LocationId = location.Id;
                        }

                        this.updateValues();
                        element.siblings().removeClass("active");
                        element.addClass("active");
                    })
                    .append($("<td>", { html: Localization.resourceStrings.SiteWizardModal_EditRoadsReplaceRoadTitle.replace("{0}", location.Title).replace("{1}", location.SequenceNumber.toString()), "class": "valueColumn" }))
                    .append($("<td>", { html: Localization.resourceStrings.SiteWizardModal_EditRoadsReplaceRoadDescription.replace("{0}", location.Title).replace("{1}", location.SequenceNumber.toString()) })));
        }

        this.updateValues();

        setTimeout(() => {
            if (tbody.children("tr.active").length === 0) {
                tbody.children("tr").first().click();
            } else {
                tbody.children("tr.active").first().click();
            }
        }, 0);
    }

    // create list of components
    private createCaptureDevicesList(): void {

        let devices: WizardDeviceInfoViewModel[] = this.wizardInfo.Devices.filter(
            (device) => { return device.Category === WizardDeviceCategory.Capturing; }
        );

        // add hard shoulder control element 
        let hasHardShoulderInput: JQuery =
            $("<input>", { id: "wizardHasHardShoulderCheckbox", "class": "bootstrap-checkbox", type: "checkbox" })
                .prop("checked", this.wizardViewModel.HasHardShoulder)
                .click(eventObject => { this.wizardViewModel.HasHardShoulder = $(eventObject.target).prop("checked") });

        let addHardShoulderButton: JQuery = $("<div>", { "class": "hideInWizardVpsMode", style: "display:none;" })
            .append(hasHardShoulderInput)
            .append($("<label>", { "class": "customLabel", "style": "margin-top: 1em;", "for": "wizardHasHardShoulderCheckbox", html: Localization.resourceStrings.SiteWizardModal_EditHasHardShoulderTitle }));

        // update device count for multiple different devices of same device type
        if (this.wizardViewModel.CapturingDevices != null) {
            for (let device of devices) {
                var modelDevice = this.wizardViewModel.CapturingDevices.filter(d => { return d.Title === device.Title && d.LocationTypeName === device.LocationTypeName; });
                if (modelDevice.length > 0) {
                    device.Count = modelDevice[0].Count;
                }
            }
        }

        // update model
        this.wizardViewModel.CapturingDevices = devices;
        this.updateCaptureDeviceTypeIds(devices);

        // update page content
        if (this.currentPage === WizardPage.Devices) {
            let tbody: JQuery = $("<tbody>");
            this.editArea.html("")
                .append($("<h4>", { html: Localization.resourceStrings.SiteWizardModal_EditDevicesTitle }))
                .append($("<table>", { "class": "table table-noTopBorder margin-bottom-0" })
                    .append($("<thead>")
                        .append($("<tr>")
                            .append($("<th>", { html: Localization.resourceStrings.SiteWizardModal_EditTableTypeTitle }))
                            .append($("<th>", { html: Localization.resourceStrings.SiteWizardModal_EditTableNameTitle }))
                            .append($("<th>", { html: Localization.resourceStrings.SiteWizardModal_EditTableCountTitle }))))
                    .append(tbody))
                .append(addHardShoulderButton);

            for (var deviceTypeId of this.captureDeviceTypeIds) {
                let groupDevices = devices.filter(d => { return d.DeviceType.Id === deviceTypeId; });

                for (let i: number = 0; i < groupDevices.length; i++) {
                    let row: JQuery = $("<tr>", { "data-id": deviceTypeId });
                    tbody
                        .append(row);
                    if (i === 0) {
                        row.append($("<td>", {
                            rowspan: groupDevices.length,
                            html:
                            `<span><img class="element-image" src="/api/Info/GetIcon/${groupDevices[i].DeviceType.IconId}"></span>` +
                            groupDevices[i].Name
                        }));
                    }

                    row
                        .append($("<td>", { html: groupDevices[i].Title }))
                        .append(this.createCountColumn(groupDevices[i]));
                }
            }

            // update and validate values
            this.updateValues();
        }
    }

    // creates list of axle counting elements
    private createAxleDevicesList(): void {
        let devices: WizardDeviceInfoViewModel[] = this.wizardInfo.Devices.filter(
            (device) => { return device.Category === WizardDeviceCategory.AxleDetection && device.LocationTypeName === this.currentLocationType; }
        );

        // get device count
        if (this.wizardViewModel.AxleDetectionDevices != null) {
            for (let device of devices) {
                var modelDevice = this.wizardViewModel.AxleDetectionDevices.filter(d => { return d.DeviceType.Id === device.DeviceType.Id; });
                if (modelDevice.length > 0) {
                    device.Count = modelDevice[0].Count;
                }
            }
        }

        // update model
        this.wizardViewModel.AxleDetectionDevices = devices;

        // update page content
        if (this.currentPage === WizardPage.AxleDetection) {
            let tbody: JQuery = $("<tbody>");

            this.editArea.html("")
                .append($("<h4>", { html: Localization.resourceStrings.SiteWizardModal_EditAxleDetectionTitle }))
                .append($("<table>", { "class": "table table-noTopBorder margin-bottom-0" })
                    .append($("<thead>")
                        .append($("<tr>")
                            .append($("<th>", { html: Localization.resourceStrings.SiteWizardModal_EditTableNameTitle }))
                            .append($("<th>", { html: Localization.resourceStrings.SiteWizardModal_EditTableCountTitle }))))
                    .append(tbody));

            for (let device of devices) {
                if (device.DeviceType != null) {
                    tbody
                        .append($("<tr>", { "data-id": device.DeviceType.Id })
                            .append($("<td>", { html: `<span><img class="element-image" src="/api/Info/GetIcon/${device.DeviceType.IconId}"></span> ${device.Title}` }))
                            .append(this.createCountColumn(device)));
                }
            }

            this.updateValues();
        }
    }

    // update list of available device types
    private updateCaptureDeviceTypeIds(devices: WizardDeviceInfoViewModel[]): void {
        for (let device of devices) {
            if (device.DeviceType != null) {
                if (this.captureDeviceTypeIds.indexOf(device.DeviceType.Id) === -1) {
                    this.captureDeviceTypeIds.push(device.DeviceType.Id);
                }
            }
        }
    }

    // navigate to page directly
    private goToSpecificPage(page: WizardPage, target: JQuery): void {

        if (target.hasClass(SiteWizardView.WIZARD_INDEX_CLASS)) {
            switch (page) {
                case WizardPage.Locations:
                    this.showLocationEdit();
                    break;
                case WizardPage.Region:
                    this.showRegionEdit();
                    break;
                case WizardPage.Devices:
                    this.showDeviceEdit();
                    break;
                case WizardPage.Classifier:
                    this.showClassifierEdit();
                    break;
                case WizardPage.AxleDetection:
                    this.showAxleDetectionEdit();
                    break;
                case WizardPage.VpsMeasurements:
                    this.showVpsMeasurementsEdit();
                    break;
                case WizardPage.FpsOptions:
                    this.showFpsOptionsEdit();
                    break;
                default:
                    this.showLocationEdit();
                    break;
            }
        }
    }

    // navigate to next page
    private goToNextPage(): void {

        switch (this.currentPage) {
            case WizardPage.Locations:
                this.showDeviceEdit();
                break;
            case WizardPage.Devices:
                switch (this.currentMode) {
                    case WizardMode.Tic:
                        this.showAxleDetectionEdit();
                        break;
                    case WizardMode.Tic501:
                    case WizardMode.Vps:
                        this.showRegionEdit();
                        break;
                    case WizardMode.Fps:
                        this.showFpsOptionsEdit();
                        break;
                    case WizardMode.None:
                    default:
                        this.showDeviceEdit();
                        break;
                }

                break;
            case WizardPage.AxleDetection:
            case WizardPage.FpsOptions:
                this.showRegionEdit();
                break;
            case WizardPage.Region:
                switch (this.currentMode) {
                    case WizardMode.Fps:
                    case WizardMode.Tic:
                    case WizardMode.Tic501:
                        this.showClassifierEdit();
                        break;
                    case WizardMode.Vps:
                        this.showVpsMeasurementsEdit();
                        break;
                    case WizardMode.None:
                    default:
                        this.showDeviceEdit();
                        break;
                }

                break;
            case WizardPage.Classifier:
            case WizardPage.VpsMeasurements:
                // there is no next page
                break;
            default:
                // show first page on default
                this.showLocationEdit();
                break;
        }
    }

    // navigate to previous page
    private goToPrevPage(): void {

        switch (this.currentPage) {
            case WizardPage.Locations:
                this.showLocationEdit();
                break;
            case WizardPage.Devices:
                this.showLocationEdit();
                break;
            case WizardPage.AxleDetection:
                this.showDeviceEdit();
                break;
            case WizardPage.Region:
                switch (this.currentMode) {
                    case WizardMode.Tic:
                        this.showAxleDetectionEdit();
                        break;
                    case WizardMode.Fps:
                        this.showFpsOptionsEdit();
                        break;
                    case WizardMode.Tic501:
                    case WizardMode.Vps:
                    case WizardMode.None:
                    default:
                        this.showDeviceEdit();
                        break;

                }

                break;
            case WizardPage.Classifier:
                this.showRegionEdit();
                break;
            case WizardPage.VpsMeasurements:
                this.showRegionEdit();
                break;
            case WizardPage.FpsOptions:
                this.showDeviceEdit();
                break;
            default:
                // show first page on default
                this.showLocationEdit();
                break;
        }
    }

    // enable/disable count control elements depending on current count value and count limits
    private toggleCountEnabled(): void {
        if (this.currentPage === WizardPage.Devices) {
            let tbody: JQuery = this.editArea.find("tbody");
            let activeDevices = this.wizardViewModel.CapturingDevices != null ?
                this.wizardViewModel.CapturingDevices.filter(device => { return device.Count > 0; }) :
                [];
            let deviceTypeId: string = this.getActiveDeviceTypeId();

            if (activeDevices.length === 0) {
                tbody.find(".wizardIncreaseCount").removeClass("iconDisabled");
                tbody.find(".wizardDecreaseCount").addClass("iconDisabled");
            } else {
                let sum: number = 0;
                for (var device of activeDevices) {
                    sum += device.Count;
                }

                let enabled: JQuery = tbody.find(`tr[data-id='${deviceTypeId}']`).find(".countControlElement");
                let disabled: JQuery = tbody.find(`tr[data-id!='${deviceTypeId}']`).find(".countControlElement");
                enabled.each((index: number, element: Element) => {
                    $(element).find(".wizardIncreaseCount").toggleClass("iconDisabled", sum >= this.wizardInfo.MaxDevicesCount || $(element).hasClass("maxCountReached"));
                    $(element).find(".wizardDecreaseCount").toggleClass("iconDisabled", $(element).find(".countValue").html() === "0");
                });
                enabled.find(".wizardDecreaseCount").removeClass("iconDisabled");
                disabled.find(".wizardIncreaseCount").addClass("iconDisabled");
                disabled.find(".wizardDecreaseCount").addClass("iconDisabled");
            }
        } else if (this.currentPage === WizardPage.AxleDetection) {
            let axleCounterCountElement: JQuery = this.editArea.find("tbody").find(".countControlElement");
            axleCounterCountElement.find(".wizardIncreaseCount").toggleClass("iconDisabled", axleCounterCountElement.hasClass("maxCountReached"));
            axleCounterCountElement.find(".wizardDecreaseCount").toggleClass("iconDisabled", axleCounterCountElement.find(".countValue").html() === "0");
        }
    }

    // enables/disables direct page navigation via index entries
    private toggleIndexClick(): void {

        let devices: boolean = this.validityStates.Locations;
        let fpsOptions: boolean = devices && this.validityStates.Components && this.currentMode === WizardMode.Fps;
        let axleDetection: boolean = devices && this.validityStates.Components && this.currentMode === WizardMode.Tic;
        let regionFps: boolean = fpsOptions && this.validityStates.FpsOptions;
        let regionAxle: boolean = axleDetection && this.validityStates.AxleDetection;
        let regionDirect: boolean = devices && this.validityStates.Components && this.currentMode !== WizardMode.Fps && this.currentMode !== WizardMode.Tic && this.currentMode !== WizardMode.None;
        let region: boolean = regionAxle || regionFps || regionDirect;
        let classifier: boolean = region && this.validityStates.Region && this.currentMode !== WizardMode.Vps && this.currentMode !== WizardMode.None;
        let vpsMeasurements: boolean = region && this.validityStates.Region && this.currentMode === WizardMode.Vps;

        this.wizardModal.find("#modalWizardRoads").addClass(SiteWizardView.WIZARD_INDEX_CLASS);
        this.wizardModal.find("#modalWizardDevices").toggleClass(SiteWizardView.WIZARD_INDEX_CLASS, devices);
        this.wizardModal.find("#modalWizardFpsOptions").toggleClass(SiteWizardView.WIZARD_INDEX_CLASS, fpsOptions);
        this.wizardModal.find("#modalWizardAxleDetection").toggleClass(SiteWizardView.WIZARD_INDEX_CLASS, axleDetection);
        this.wizardModal.find("#modalWizardRegion").toggleClass(SiteWizardView.WIZARD_INDEX_CLASS, region);
        this.wizardModal.find("#modalWizardClassifier").toggleClass(SiteWizardView.WIZARD_INDEX_CLASS, classifier);
        this.wizardModal.find("#modalWizardVpsMeasurements").toggleClass(SiteWizardView.WIZARD_INDEX_CLASS, vpsMeasurements);
    }

    // enables/disables direct page navigation via index entries
    private disableIndexClick(): void {
        this.wizardModal.find("#modalWizardRoads").removeClass(SiteWizardView.WIZARD_INDEX_CLASS);
        this.wizardModal.find("#modalWizardDevices").removeClass(SiteWizardView.WIZARD_INDEX_CLASS);
        this.wizardModal.find("#modalWizardFpsOptions").removeClass(SiteWizardView.WIZARD_INDEX_CLASS);
        this.wizardModal.find("#modalWizardAxleDetection").removeClass(SiteWizardView.WIZARD_INDEX_CLASS);
        this.wizardModal.find("#modalWizardRegion").removeClass(SiteWizardView.WIZARD_INDEX_CLASS);
        this.wizardModal.find("#modalWizardClassifier").removeClass(SiteWizardView.WIZARD_INDEX_CLASS);
        this.wizardModal.find("#modalWizardVpsMeasurements").removeClass(SiteWizardView.WIZARD_INDEX_CLASS);
    }

    // enables/disables page navigation and finish buttons depending on current page and validity state
    private toggleButtonEnabled(): void {

        let prevButton: JQuery = this.wizardModal.find("#modalWizardPrevPageButton");
        let nextButton: JQuery = this.wizardModal.find("#modalWizardNextPageButton");
        let finishButton: JQuery = this.wizardModal.find("#modalWizardCreateSiteButton");

        switch (this.currentPage) {
            case WizardPage.Locations:
                prevButton.hide();
                nextButton.show();
                nextButton.prop("disabled", !this.validityStates.Locations);
                finishButton.hide();
                break;
            case WizardPage.Devices:
                prevButton.show();
                nextButton.show();
                nextButton.prop("disabled", !this.validityStates.Components);
                finishButton.hide();
                break;
            case WizardPage.AxleDetection:
                prevButton.show();
                prevButton.prop("disabled", false);
                nextButton.show();
                nextButton.prop("disabled", !this.validityStates.AxleDetection);
                finishButton.hide();
                break;
            case WizardPage.Region:
                prevButton.show();
                prevButton.prop("disabled", false);
                nextButton.show();
                nextButton.prop("disabled", !this.validityStates.Region);
                finishButton.hide();
                break;
            case WizardPage.Classifier:
                prevButton.show();
                prevButton.prop("disabled", false);
                nextButton.hide();
                finishButton.show();
                finishButton.prop("disabled", !this.getValidState());
                break;
            case WizardPage.VpsMeasurements:
                prevButton.show();
                prevButton.prop("disabled", false);
                nextButton.hide();
                finishButton.show();
                finishButton.prop("disabled", !this.getValidState());
                break;
            case WizardPage.FpsOptions:
                prevButton.show();
                prevButton.prop("disabled", false);
                nextButton.show();
                nextButton.prop("disabled", !this.validityStates.FpsOptions);
                finishButton.hide();
                break;
            default:
                // hide all buttons by default
                prevButton.hide();
                nextButton.hide();
                finishButton.hide();
                break;
        }
    }

    // sets the current mode based on active capturing devices. The Mode controls accessable pages and index content
    private toggleWizardMode(): void {
        let activeDevices: WizardDeviceInfoViewModel[] = [];

        if (this.wizardViewModel.CapturingDevices != null) {
            activeDevices = this.wizardViewModel.CapturingDevices.filter(device => { return device.Count > 0; });
        }

        this.wizardModal.find("tbody > tr.wizardFps").css("display", "none");
        this.wizardModal.find("tbody > tr.wizardTic").css("display", "none");
        this.wizardModal.find("tbody > tr.wizardVps").css("display", "none");
        this.wizardModal.find(".hideInWizardVpsMode").css("display", "none");
        this.currentMode = WizardMode.None;

        if (activeDevices.length > 0) {
            switch (activeDevices[0].Name) {
                case "Fps":
                    this.wizardModal.find("tbody > tr.wizardFps").css("display", "table-row");
                    this.wizardModal.find(".hideInWizardVpsMode").css("display", "inline");
                    this.currentMode = WizardMode.Fps;
                    break;
                case "Vps153":
                case "Vps553":
                    this.wizardModal.find("tbody > tr.wizardVps").css("display", "table-row");
                    this.wizardViewModel.AxleDetectionDevices = null;

                    this.currentMode = WizardMode.Vps;
                    break;
                case "Tic102":
                case "Tic502":
                    this.wizardModal.find("tbody > tr.wizardTic").css("display", "table-row");
                    this.wizardModal.find(".hideInWizardVpsMode").css("display", "inline");
                    this.currentMode = WizardMode.Tic;
                    break;
                case "Tic501":
                    this.wizardModal.find("tbody > tr.wizardTic").css("display", "table-row");
                    this.wizardModal.find("tbody > tr#modalWizardAxleDetection").css("display", "none");
                    this.wizardModal.find(".hideInWizardVpsMode").css("display", "inline");
                    this.currentMode = WizardMode.Tic501;
                    break;
            }
        } 
    }

    // returns device type id of a device type that has a count higher than zero
    private getActiveDeviceTypeId(): string {

        const activeDevices = this.wizardViewModel.CapturingDevices.filter(device => { return device.Count > 0; });

        if (activeDevices.length === 0) {
            this.currentMode = WizardMode.None;
            this.currentLocationType = "";
            return "";
        }

        let valid = true;
        for (let i: number = 1; i < activeDevices.length; i++) {
            if (activeDevices[0].DeviceType.Id !== activeDevices[i].DeviceType.Id) {
                valid = false;
                break;
            }
        }

        if (valid) {
            if (this.lastActiveDeviceTypeId !== activeDevices[0].DeviceType.Id) {
                if (this.lastActiveDeviceTypeId != null) {
                    this.wizardViewModel.AxleDetectionDevices = null;
                    this.selectedClassifierInfo = null;
                    this.selectedCategorySystemInfo = null;
                }

                this.lastActiveDeviceTypeId = activeDevices[0].DeviceType.Id;
            }

            this.currentLocationType = activeDevices[0].LocationTypeName;
            return activeDevices[0].DeviceType.Id;
        }

        return "";
    }
}

enum WizardPage {
    Locations,
    Devices,
    AxleDetection,
    Region,
    Classifier,
    VpsMeasurements,
    FpsOptions
}

enum DataFileType {
    Region,
    Classifier,
    CategorySystem,
}
